import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Trash2, MessageCircle, Upload, X, ChevronsUpDown, Car, Plus, Wrench,
  CalendarClock, FileText, Download, ExternalLink, UserRound, ClipboardList,
  CheckCircle2, Phone,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ComboBoxCreatable } from "@/components/ComboBoxCreatable";
import { BRANDS, MODELS_BY_BRAND, COLORS, mergedOptions, bumpRecent, splitCarModel, joinCarModel } from "@/lib/vehicleCatalog";
import { ColorChip } from "@/components/ColorChip";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";
import { PhotoLightbox } from "@/components/PhotoLightbox";

const isoLocal = (d) => {
  const t = new Date(d);
  t.setMinutes(t.getMinutes() - t.getTimezoneOffset());
  return t.toISOString().slice(0, 16);
};

const appointmentDateLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Datum wählen";
  return date.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const emptyForm = () => ({
  terminart: "arbeitstermin",
  customer_id: "", customer_name: "", phone: "", email: "",
  car_model: "", plate: "", address: "",
  // Sprint 20 — split vehicle fields for fast phone-booking entry
  vehicle_brand: "", vehicle_model: "", vehicle_year: "", vehicle_color: "",
  vehicle_id: "",
  service_id: "", service_name: "", color: "#3377FF",
  services: [],
  start: isoLocal(new Date()),
  end: isoLocal(new Date(Date.now() + 60 * 60000)),
  estimated_price: 0, final_price: 0,
  payment_status: "unpaid", payment_method: "",
  notes: "",
});

function AppointmentSection({ number, icon: Icon, title, hint, children, className = "" }) {
  return (
    <section className={`stellar-appointment-section ${className}`}>
      <div className="stellar-appointment-section-heading">
        <span className="stellar-appointment-step">{number}</span>
        <span className="stellar-appointment-section-icon"><Icon className="h-4 w-4" /></span>
        <div className="min-w-0">
          <h3 className="font-display text-base font-bold leading-tight" title={hint}>{title}</h3>
        </div>
      </div>
      <div className="stellar-appointment-section-content">{children}</div>
    </section>
  );
}

// Sprint 14 — Kostenvoranschlag summary card (visible on saved Begutachtung)
function QuoteSection({ appointment, services, onOpen, onChanged }) {
  const q = appointment.quote;
  const token = typeof window !== "undefined" ? window.localStorage.getItem("stellar_token") : "";
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const pdfUrl = q ? `${backend}/api/appointments/${appointment.id}/quote/pdf?auth=${encodeURIComponent(token)}` : "";

  const deleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/appointments/${appointment.id}/quote`)).data,
    onSuccess: () => { toast.success("Kostenvoranschlag gelöscht"); onChanged?.(); },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!q) {
    return (
      <div className="rounded-md border border-dashed border-border/60 p-3" data-testid="quote-empty">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <div className="text-sm text-muted-foreground">Noch kein Kostenvoranschlag.</div>
          <Button type="button" size="sm" variant="outline" className="ml-auto" onClick={onOpen} data-testid="quote-create">
            <Plus className="h-3.5 w-3.5 mr-1" /> Kostenvoranschlag erstellen
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2" data-testid="quote-summary">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">✓</span>
        <div className="text-sm">
          <span className="font-medium">Kostenvoranschlag erstellt</span>
          <div className="text-xs text-muted-foreground font-mono" data-testid="quote-number">{q.quote_number}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-sm font-semibold" data-testid="quote-total">{fmtEur(q.total)}</div>
          <div className="text-[10px] text-muted-foreground">{q.lines?.length || 0} Position{(q.lines?.length || 0) === 1 ? "" : "en"}{q.valid_until ? ` · gültig bis ${q.valid_until}` : ""}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border/60 hover:bg-muted" data-testid="quote-open-pdf">
          <ExternalLink className="h-3 w-3" /> PDF öffnen
        </a>
        <a href={pdfUrl} download={`${q.quote_number}.pdf`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border/60 hover:bg-muted" data-testid="quote-download-pdf">
          <Download className="h-3 w-3" /> Herunterladen
        </a>
        <button type="button" onClick={onOpen} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border/60 hover:bg-muted" data-testid="quote-edit">
          <Wrench className="h-3 w-3" /> Bearbeiten
        </button>
        <button
          type="button"
          onClick={() => { if (confirm("Kostenvoranschlag löschen?")) deleteMut.mutate(); }}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-destructive/40 text-destructive hover:bg-destructive/10 ml-auto"
          data-testid="quote-delete"
        >
          <Trash2 className="h-3 w-3" /> Löschen
        </button>
      </div>
    </div>
  );
}

function QuoteDialog({ appointment, services, onClose, onSaved }) {
  const initial = appointment.quote;
  const [lines, setLines] = useState(
    initial?.lines?.length
      ? initial.lines.map((l) => ({ service_id: l.service_id || null, service_name: l.service_name || "", description: l.description || "", quantity: l.quantity || 1, unit_price: l.unit_price || 0 }))
      : (appointment.services || []).map((s) => ({ service_id: s.service_id || null, service_name: s.service_name || "", description: "", quantity: 1, unit_price: s.price || 0 }))
  );
  const [notes, setNotes] = useState(initial?.notes ?? (appointment.notes || ""));
  const [validUntil, setValidUntil] = useState(initial?.valid_until || (() => {
    const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10);
  })());

  const addFromCatalog = (s) => setLines((prev) => [...prev, {
    service_id: s.id, service_name: s.name, description: "", quantity: 1, unit_price: Number(s.default_price) || 0,
  }]);
  const addCustom = () => setLines((prev) => [...prev, { service_id: null, service_name: "", description: "", quantity: 1, unit_price: 0 }]);
  const update = (i, k, v) => setLines((prev) => prev.map((row, idx) => idx === i ? { ...row, [k]: k === "quantity" || k === "unit_price" ? (Number(v) || 0) : v } : row));
  const remove = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const subtotal = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const saveMut = useMutation({
    mutationFn: async () => (await api.put(`/appointments/${appointment.id}/quote`, {
      lines: lines.map((l) => ({
        service_id: l.service_id || null,
        service_name: l.service_name || "",
        description: l.description || "",
        quantity: Number(l.quantity) || 0,
        unit_price: Number(l.unit_price) || 0,
      })),
      notes,
      valid_until: validUntil || null,
    })).data,
    onSuccess: () => { toast.success("Kostenvoranschlag gespeichert"); onSaved?.(); onClose(); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText className="h-4 w-4" /> Kostenvoranschlag
            {initial?.quote_number && <span className="text-xs font-mono text-muted-foreground">· {initial.quote_number}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Positionen ({lines.length})</Label>
            <ServicePicker services={services} onPick={addFromCatalog} onCustom={addCustom} />
          </div>

          {lines.length === 0 ? (
            <div className="text-xs text-muted-foreground italic px-3 py-4 border border-dashed rounded-md text-center">
              Noch keine Positionen. Aus dem Katalog auswählen oder eigene Position hinzufügen.
            </div>
          ) : (
            <div className="space-y-2">
              {lines.map((ln, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_60px_100px_auto] items-center gap-2" data-testid={`quote-line-${idx}`}>
                  <Input
                    value={ln.service_name}
                    onChange={(e) => update(idx, "service_name", e.target.value)}
                    placeholder="Leistungsbezeichnung"
                    className="h-8"
                    data-testid={`quote-line-name-${idx}`}
                  />
                  <Input
                    type="number" step="0.1" value={ln.quantity}
                    onChange={(e) => update(idx, "quantity", e.target.value)}
                    className="h-8" placeholder="Menge"
                    data-testid={`quote-line-qty-${idx}`}
                  />
                  <Input
                    type="number" step="0.01" value={ln.unit_price}
                    onChange={(e) => update(idx, "unit_price", e.target.value)}
                    className="h-8" placeholder="€"
                    data-testid={`quote-line-price-${idx}`}
                  />
                  <Button type="button" size="icon" variant="ghost" onClick={() => remove(idx)} data-testid={`quote-line-remove-${idx}`}>
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Gültig bis</Label>
              <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="quote-valid-until" />
            </div>
            <div className="text-right self-end">
              <div className="text-xs text-muted-foreground">Zwischensumme</div>
              <div className="text-xl font-semibold" data-testid="quote-subtotal">{fmtEur(subtotal)}</div>
            </div>
          </div>

          <div>
            <Label>Anmerkungen</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="z.B. Für Versicherung / Arbeitgeber…" data-testid="quote-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || lines.length === 0} data-testid="quote-save">
            {saveMut.isPending ? "Speichern…" : (initial ? "Aktualisieren & PDF" : "Erstellen & PDF")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ServicePicker({ services = [], onPick, onCustom }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm" data-testid="appt-add-service">
          <Plus className="h-3.5 w-3.5 mr-1" /> Leistung hinzufügen
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Katalog durchsuchen…" />
          <CommandList
            className="h-64 max-h-[45vh] overflow-y-scroll overscroll-contain touch-pan-y"
            style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y", overscrollBehavior: "contain" }}
            onWheelCapture={(event) => event.stopPropagation()}
            onTouchMoveCapture={(event) => event.stopPropagation()}
            data-testid="appt-service-options"
          >
            <CommandEmpty>Keine Treffer</CommandEmpty>
            <CommandGroup heading="Aus Katalog">
              {services.map((s) => (
                <CommandItem
                  key={s.id}
                  value={s.name}
                  onSelect={() => { onPick(s); setOpen(false); }}
                  data-testid={`appt-picker-service-${s.id}`}
                >
                  <span className="h-2.5 w-2.5 rounded-full mr-2" style={{ background: s.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{s.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {s.default_duration_min} Min{Number(s.default_price) > 0 ? ` · ${fmtEur(s.default_price)}` : ""}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup>
              <CommandItem
                value="custom-free-text"
                onSelect={() => { onCustom?.(); setOpen(false); }}
                data-testid="appt-picker-custom"
              >
                <Plus className="h-3.5 w-3.5 mr-2" /> Eigene Leistung (Freitext)
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CustomerPicker({ value, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data: customers = [] } = useQuery({
    queryKey: ["customers-picker", q],
    queryFn: async () => (await api.get(`/customers?q=${encodeURIComponent(q)}`)).data,
  });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid="customer-picker"
          className="w-full justify-between font-normal"
        >
          {value || "Kunde auswählen oder neu eingeben"}
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Kunde suchen…" value={q} onValueChange={setQ} />
          <CommandList>
            <CommandEmpty>Keine Treffer</CommandEmpty>
            <CommandGroup>
              {customers.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  onSelect={() => { onPick(c); setOpen(false); }}
                  data-testid={`picker-customer-${c.id}`}
                >
                  <div>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.phone} · {c.plates?.join(", ")}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function PhotoUploader({ appointmentId, photos, onChanged, readOnly = false }) {
  const [lightboxId, setLightboxId] = useState(null);
  const imagePhotos = (photos || []).filter((p) => p.content_type?.startsWith("image/"));
  const upload = async (e, category) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      try {
        await api.post(`/appointments/${appointmentId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (err) {
        toast.error(apiError(err));
      }
    }
    onChanged?.();
    e.target.value = "";
  };

  const remove = async (pid) => {
    try {
      await api.delete(`/appointments/${appointmentId}/photos/${pid}`);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const cats = [
    { key: "before", label: "Vorher" },
    { key: "after", label: "Nachher" },
    { key: "documents", label: "Dokumente" },
  ];

  return (
    <div className="space-y-3">
      {cats.map((c) => {
        const list = (photos || []).filter((p) => p.category === c.key);
        return (
          <div key={c.key}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{c.label}</div>
              {!readOnly && (
                <label className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline" data-testid={`upload-${c.key}`}>
                  <Upload className="h-3 w-3" /> Hochladen
                  <input type="file" multiple className="hidden" accept="image/*,application/pdf" onChange={(e) => upload(e, c.key)} />
                </label>
              )}
            </div>
            {list.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">Keine Dateien</div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {list.map((p) => (
                  <div key={p.id} className="relative group">
                    {p.content_type?.startsWith("image/") ? (
                      <button
                        type="button"
                        className="block w-full cursor-zoom-in"
                        onClick={() => setLightboxId(p.id)}
                        aria-label={`${p.filename || "Foto"} vergrößern`}
                      >
                        <img src={fileUrl(p.storage_path)} alt={p.filename} className="w-full h-24 object-cover rounded-md border transition-transform hover:scale-[1.02]" />
                      </button>
                    ) : (
                      <a href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer" className="block h-24 rounded-md border flex items-center justify-center text-xs bg-muted">
                        {p.filename}
                      </a>
                    )}
                    {!readOnly && (
                      <button
                        onClick={() => remove(p.id)}
                        className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"
                        data-testid={`delete-photo-${p.id}`}
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <PhotoLightbox photos={imagePhotos} activeId={lightboxId} onClose={() => setLightboxId(null)} />
    </div>
  );
}

function VehiclePicker({ customerId, value, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles-picker", customerId],
    queryFn: async () => (await api.get("/vehicles", { params: { customer_id: customerId }})).data,
    enabled: !!customerId,
  });
  if (!customerId) {
    return (
      <div className="text-xs text-muted-foreground italic px-3 py-2 border border-dashed rounded-md">
        Bitte zuerst Kunde auswählen
      </div>
    );
  }
  const current = vehicles.find((v) => v.id === value);
  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            data-testid="vehicle-picker"
            className="flex-1 justify-between font-normal"
          >
            <span className="inline-flex items-center gap-2 truncate">
              <Car className="h-3.5 w-3.5 text-muted-foreground" />
              {current ? current.display_name : (vehicles.length === 0 ? "Keine Fahrzeuge vorhanden" : "Fahrzeug wählen (optional)")}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Fahrzeug suchen…" />
            <CommandList>
              <CommandEmpty>Keine Fahrzeuge für diesen Kunden</CommandEmpty>
              <CommandGroup>
                {vehicles.map((v) => (
                  <CommandItem
                    key={v.id}
                    value={v.display_name}
                    onSelect={() => { onPick(v); setOpen(false); }}
                    data-testid={`picker-vehicle-${v.id}`}
                  >
                    <div>
                      <div className="text-sm font-medium">{v.manufacturer} {v.model}{v.year && <span className="font-normal text-muted-foreground"> · Bj. {v.year}</span>}</div>
                      <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                        <span>{v.license_plate || "—"}</span>
                        {v.color && (<><span>·</span><ColorChip value={v.color} testId={`vehicle-picker-color-${v.id}`} /></>)}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <Button type="button" variant="ghost" size="icon" onClick={onClear} data-testid="vehicle-clear" title="Zuordnung entfernen">
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default function AppointmentSheet({ open, onOpenChange, appointment, prefill, services, onSaved, sourceBegutachtungId }) {
  const [form, setForm] = useState(emptyForm());
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (appointment) {
      setForm({
        ...appointment,
        terminart: appointment.terminart || "arbeitstermin",
        services: appointment.services || [],
        start: isoLocal(appointment.start),
        end: isoLocal(appointment.end),
      });
    } else if (prefill) {
      const derived = splitCarModel(prefill.car_model || "");
      const normalizeServiceName = (value) => (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ß/g, "ss")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      const requestedService = prefill.service_name || "";
      const catalogService = requestedService
        ? services.find((service) => normalizeServiceName(service.name) === normalizeServiceName(requestedService))
        : null;
      const importedServices = catalogService
        ? [{
            service_id: catalogService.id,
            service_name: catalogService.name,
            price: Number(catalogService.default_price) || 0,
            duration_min: Number(catalogService.default_duration_min) || 0,
            color: catalogService.color || "#3377FF",
          }]
        : (prefill.services || []);
      const importedDuration = importedServices.reduce((sum, service) => sum + (Number(service.duration_min) || 0), 0);
      const importedPrice = importedServices.reduce((sum, service) => sum + (Number(service.price) || 0), 0);
      const importedStart = new Date(prefill.start);
      const importedEnd = importedDuration > 0
        ? new Date(importedStart.getTime() + importedDuration * 60000)
        : prefill.end;
      setForm({
        ...emptyForm(),
        terminart: prefill.terminart || "arbeitstermin",
        start: isoLocal(prefill.start),
        end: isoLocal(importedEnd),
        customer_name: prefill.customer_name || "",
        email: prefill.email || "",
        phone: prefill.phone || "",
        customer_id: prefill.customer_id || "",
        vehicle_id: prefill.vehicle_id || "",
        address: prefill.address || "",
        car_model: prefill.car_model || "",
        vehicle_brand: prefill.vehicle_brand || derived.brand,
        vehicle_model: prefill.vehicle_model || derived.model,
        vehicle_year: prefill.vehicle_year || "",
        vehicle_color: prefill.vehicle_color || "",
        color: catalogService?.color || prefill.color || "#3377FF",
        plate: prefill.plate || "",
        service_id: catalogService?.id || prefill.service_id || "",
        service_name: catalogService?.name || prefill.service_name || "",
        services: importedServices,
        estimated_price: importedPrice || Number(prefill.estimated_price) || 0,
        notes: prefill.notes || "",
      });
    } else {
      setForm(emptyForm());
    }
  }, [appointment, prefill, open, services]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const durationMin = Math.max(0, Math.round((new Date(form.end) - new Date(form.start)) / 60000));

  const addServiceFromCatalog = (s) => {
    setForm((f) => {
      const newLine = {
        service_id: s.id,
        service_name: s.name,
        price: Number(s.default_price) || 0,
        duration_min: s.default_duration_min || 0,
        color: s.color,
      };
      const next = [...(f.services || []), newLine];
      // First service dictates color + end time + service_name/id for legacy calendar rendering
      const isFirst = next.length === 1;
      const start = new Date(f.start);
      const totalDur = next.reduce((a, x) => a + (Number(x.duration_min) || 0), 0);
      const end = totalDur > 0 ? isoLocal(new Date(start.getTime() + totalDur * 60000)) : f.end;
      const sum = next.reduce((a, x) => a + (Number(x.price) || 0), 0);
      return {
        ...f,
        services: next,
        color: isFirst ? s.color : f.color,
        service_id: isFirst ? s.id : f.service_id,
        service_name: next.map((x) => x.service_name).filter(Boolean).join(", "),
        estimated_price: sum || f.estimated_price,
        end,
      };
    });
  };

  const addCustomService = () => {
    setForm((f) => ({
      ...f,
      services: [...(f.services || []), { service_id: null, service_name: "", price: 0, duration_min: 0, color: f.color }],
    }));
  };

  const updateServiceLine = (idx, key, value) => {
    setForm((f) => {
      const list = [...(f.services || [])];
      list[idx] = { ...list[idx], [key]: key === "price" || key === "duration_min" ? Number(value) || 0 : value };
      const sum = list.reduce((a, x) => a + (Number(x.price) || 0), 0);
      return {
        ...f,
        services: list,
        service_name: list.map((x) => x.service_name).filter(Boolean).join(", "),
        estimated_price: sum,
      };
    });
  };

  const removeServiceLine = (idx) => {
    setForm((f) => {
      const list = (f.services || []).filter((_, i) => i !== idx);
      const sum = list.reduce((a, x) => a + (Number(x.price) || 0), 0);
      const first = list[0];
      return {
        ...f,
        services: list,
        service_name: list.map((x) => x.service_name).filter(Boolean).join(", "),
        service_id: first?.service_id || "",
        color: first?.color || f.color,
        estimated_price: sum,
      };
    });
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      // Sprint 20 — brand/model/color drive the payload; car_model stays derived for legacy consumers.
      const car_model = joinCarModel(form.vehicle_brand, form.vehicle_model);
      const payload = {
        ...form,
        car_model,
        vehicle_brand: (form.vehicle_brand || "").trim(),
        vehicle_model: (form.vehicle_model || "").trim(),
        vehicle_year: form.vehicle_year ? Number(form.vehicle_year) : null,
        vehicle_color: (form.vehicle_color || "").trim(),
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
        estimated_price: Number(form.estimated_price) || 0,
        final_price: Number(form.final_price) || 0,
        customer_id: form.customer_id || null,
        vehicle_id: form.vehicle_id || null,
        service_id: form.service_id || null,
        services: (form.services || []).map((s) => ({
          service_id: s.service_id || null,
          service_name: s.service_name || "",
          price: Number(s.price) || 0,
          duration_min: Number(s.duration_min) || 0,
          color: s.color || "#3377FF",
        })),
      };
      // Record recency locally so future bookings are faster.
      if (payload.vehicle_brand) bumpRecent("brand", payload.vehicle_brand);
      if (payload.vehicle_model) bumpRecent(`model:${payload.vehicle_brand}`, payload.vehicle_model);
      if (payload.vehicle_color) bumpRecent("color", payload.vehicle_color);
      if (appointment?.id) return (await api.put(`/appointments/${appointment.id}`, payload)).data;
      const created = (await api.post(`/appointments`, payload)).data;
      // Sprint 22 — if we came from a Begutachtung, link it to the freshly saved Arbeitstermin.
      if (sourceBegutachtungId && created?.id && (payload.terminart || "arbeitstermin") === "arbeitstermin") {
        try {
          await api.post(`/appointments/${sourceBegutachtungId}/link-arbeitstermin/${created.id}`);
        } catch (err) {
          // Non-fatal — the Arbeitstermin exists; only the linkage failed.
          toast.error("Termin gespeichert, Verknüpfung zur Begutachtung fehlgeschlagen.");
        }
      }
      return created;
    },
    onSuccess: () => {
      toast.success(appointment ? "Termin aktualisiert" : "Termin erstellt");
      qc.invalidateQueries();
      onSaved?.();
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e)),
    onError: (e) => toast.error(apiError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/appointments/${appointment.id}`)).data,
    onSuccess: () => {
      toast.success("Termin gelöscht");
      qc.invalidateQueries();
      onSaved?.();
      onOpenChange(false);
    },
  });

  const convertMut = useMutation({
    mutationFn: async () => (await api.post(`/appointments/${appointment.id}/convert-to-job`)).data,
    onSuccess: (job) => {
      toast.success("Zu Auftrag umgewandelt");
      qc.invalidateQueries({ queryKey: ["jobs"] });
      onOpenChange(false);
      navigate(`/auftraege/${job.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  // Sprint 22 — Begutachtung → Arbeitstermin: no auto-create. Close current sheet
  // and open the appointment form prefilled with all Begutachtung data. The user
  // reviews & confirms before saving. Linkage is created after save.
  // Sprint 22c — wait for the current Sheet's close animation to complete before
  // navigating, otherwise Radix leaves the old overlay portal mounted on top of
  // the freshly opened sheet, creating a persistent dark backdrop.
  const openArbeitsPrefill = () => {
    if (!appointment) return;
    const bid = appointment.id;
    const params = new URLSearchParams({
      new: "1",
      terminart: "arbeitstermin",
      from_begutachtung: bid,
    });
    onOpenChange(false);
    setTimeout(() => navigate(`/kalender?${params.toString()}`), 320);
  };

  // Sprint 14 — Kostenvoranschlag dialog state
  const [quoteOpen, setQuoteOpen] = useState(false);

  const sendWhatsApp = (kind = "confirm") => {
    let phone = (form.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Keine Telefonnummer"); return; }
    if (phone.startsWith("00")) phone = phone.slice(2);
    else if (phone.startsWith("0")) phone = `49${phone.slice(1)}`;
    const dt = new Date(form.start);
    const datum = dt.toLocaleDateString("de-DE");
    const uhr = dt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const appointmentDay = new Date(dt);
    appointmentDay.setHours(0, 0, 0, 0);
    const daysUntilAppointment = Math.round((appointmentDay - today) / 86400000);
    const reminderDate = daysUntilAppointment === 1 ? `morgen, am ${datum}` : `am ${datum}`;
    const services = (form.services || []).map((service) => service.service_name).filter(Boolean).join(", ")
      || form.service_name
      || "den vereinbarten Service";
    const fullName = (form.customer_name || "").trim().replace(/^(Herr|Frau)\s+/i, "");
    const firstName = fullName.split(/\s+/)[0] || "";
    const greeting = firstName ? `Hallo ${firstName},` : "Hallo,";
    const text = kind === "reminder"
      ? `${greeting}\n\nkurze Erinnerung: Ihr Termin für ${services} ist ${reminderDate} um ${uhr} Uhr.\n\n${daysUntilAppointment === 1 ? "Bis morgen!" : "Bis bald!"}\nCristian\nAuto Stellar Aufbereitung`
      : `${greeting}\n\nIhr Termin für ${services} ist am ${datum} um ${uhr} Uhr fest eingeplant.\n\nBis bald!\nCristian\nAuto Stellar Aufbereitung`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const { data: refetchedAppt } = useQuery({
    queryKey: ["appointment", appointment?.id],
    queryFn: async () => (await api.get(`/appointments/${appointment.id}`)).data,
    enabled: !!appointment?.id && open,
  });
  const photos = refetchedAppt?.photos ?? appointment?.photos ?? [];
  const linkedJobId = refetchedAppt?.job_id || appointment?.job_id;
  const linkedJobStatus = refetchedAppt?.job_status || appointment?.job_status;
  const isClosedJob = ["fertig", "archiviert"].includes(linkedJobStatus);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="stellar-appointment-sheet w-full sm:max-w-2xl overflow-y-auto p-0"
        onInteractOutside={(e) => { if (sourceBegutachtungId) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (sourceBegutachtungId) e.preventDefault(); }}
      >
        <SheetHeader className="stellar-appointment-header sticky top-0 z-20 border-b px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3 pr-8">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
              <CalendarClock className="h-5 w-5" />
            </span>
            <div className="min-w-0 text-left">
              <SheetTitle
                className="font-display text-xl font-bold"
                title={appointment ? "Daten prüfen und bei Bedarf aktualisieren." : "In vier klaren Schritten zur fertigen Buchung."}
              >
                {appointment ? "Termin bearbeiten" : "Neuen Termin anlegen"}
              </SheetTitle>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-4 px-3 py-4 sm:px-6 sm:py-5">
          {linkedJobId && (
            <div className="rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400 px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid="converted-to-job-badge">
              <div>
                In Auftrag umgewandelt
                {(refetchedAppt?.job_title || appointment?.job_title) && (
                  <span className="ml-1 font-medium">· {refetchedAppt?.job_title || appointment?.job_title}</span>
                )}
                <span className="ml-1 font-mono text-[10px] text-sky-600/70 dark:text-sky-300/70">
                  #{linkedJobId.slice(-6)}
                </span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 border-sky-500/50 text-sky-700 dark:text-sky-300 hover:bg-sky-500/10"
                onClick={() => { onOpenChange(false); navigate(`/auftraege/${linkedJobId}`); }}
                data-testid="open-linked-job"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Auftrag öffnen
              </Button>
            </div>
          )}

          {isClosedJob && (
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600" data-testid="appointment-readonly-badge">
              <FileText className="h-3.5 w-3.5" /> Auftrag abgeschlossen
            </div>
          )}

          <fieldset disabled={isClosedJob} className={`space-y-4 min-w-0 ${isClosedJob ? "opacity-75" : ""}`}>
          {/* Sprint 11 — Terminart selector */}
          <AppointmentSection
            number="1"
            icon={ClipboardList}
            title="Was soll geplant werden?"
            hint="Wählen Sie zuerst die passende Terminart."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="terminart-selector">
              {[
                { v: "begutachtung", label: "Begutachtung", hint: "Nur ansehen & beraten" },
                { v: "arbeitstermin", label: "Arbeitstermin", hint: "Termin für tatsächliche Arbeit" },
              ].map((t) => {
                const active = form.terminart === t.v;
                return (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => setF("terminart", t.v)}
                    data-testid={`terminart-${t.v}`}
                    title={t.hint}
                    className={`stellar-terminart-option rounded-xl border px-3 py-3 text-left transition ${active ? "is-active border-primary bg-primary/10" : "border-border/70 bg-white hover:border-primary/40"}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${active ? "border-primary bg-primary text-white" : "border-slate-300"}`}>
                        {active && <CheckCircle2 className="h-3.5 w-3.5" />}
                      </span>
                      <div className={`text-sm font-semibold ${active ? "text-primary" : ""}`}>{t.label}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </AppointmentSection>

          {appointment?.converted_to_appointment_id && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-2 text-xs" data-testid="converted-badge">
              In Arbeitstermin umgewandelt.
              <button
                type="button"
                className="ml-2 underline underline-offset-2 hover:text-emerald-800 dark:hover:text-emerald-300"
                onClick={() => { onOpenChange(false); navigate(`/kalender?open=${appointment.converted_to_appointment_id}`); }}
                data-testid="open-converted-appointment"
              >Arbeitstermin öffnen →</button>
            </div>
          )}

          <AppointmentSection
            number="2"
            icon={UserRound}
            title="Kunde und Fahrzeug"
            hint="Bestehende Daten auswählen oder einen neuen Kontakt eintragen."
          >
          <div className="space-y-4">
          {/* 1. Name — locked once the appointment has been converted into an Auftrag (Sprint 21d). */}
          <div>
            <Label>{form.terminart === "begutachtung" ? "Name" : "Kunde"}</Label>
            {(refetchedAppt?.job_id || appointment?.job_id) ? (
              <div
                className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm"
                data-testid="appt-customer-readonly"
                title="Der Kunde kann nach der Auftrags-Umwandlung nicht mehr geändert werden. Änderungen bitte im verknüpften Auftrag vornehmen."
              >
                {form.customer_id ? (
                  <Link
                    to={`/kunden/${form.customer_id}`}
                    className="truncate text-primary hover:underline"
                    onClick={() => onOpenChange(false)}
                    data-testid="appt-customer-link"
                  >
                    {form.customer_name || "—"}
                  </Link>
                ) : (
                  <span className="truncate">{form.customer_name || "—"}</span>
                )}
              </div>
            ) : (
              <>
                {form.terminart === "arbeitstermin" ? (
                  <div className="mb-3">
                    <div className="mb-1 text-xs font-medium text-muted-foreground">Bestehenden Kunden auswählen (optional)</div>
                    <CustomerPicker
                      value={form.customer_id ? form.customer_name : ""}
                      onPick={(c) => {
                        const derived = splitCarModel(c.cars?.[0] || "");
                        setForm((f) => ({
                          ...f,
                          customer_id: c.id, customer_name: c.name,
                          phone: c.phone || f.phone,
                          email: c.email || f.email,
                          address: c.address || f.address,
                          car_model: c.cars?.[0] || f.car_model,
                          vehicle_brand: f.vehicle_brand || derived.brand,
                          vehicle_model: f.vehicle_model || derived.model,
                          plate: c.plates?.[0] || f.plate,
                        }));
                      }}
                    />
                  </div>
                ) : null}
                <Input
                  placeholder={form.terminart === "begutachtung" ? "Name" : "Kundenname"}
                  data-testid="input-customer-name"
                  value={form.customer_name}
                  onChange={(e) => setF("customer_name", e.target.value)}
                />
              </>
            )}
          </div>

          {/* 2. Telefon */}
          <div>
            <Label>Telefon</Label>
            <div className="flex gap-2">
              <Input className="min-w-0" data-testid="input-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} />
              {form.phone && (
                <Button asChild type="button" variant="outline" className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50" title={`${form.phone} anrufen`}>
                  <a href={`tel:${String(form.phone).replace(/[^\d+]/g, "")}`} data-testid="call-appointment-customer">
                    <Phone className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Anrufen</span>
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* 3–5. Fahrzeugmarke · Modell · Farbe — locked once converted to Auftrag (Sprint 21e). */}
          {(refetchedAppt?.job_id || appointment?.job_id) ? (
            <div className="rounded-md border border-input bg-muted/40 px-3 py-2 space-y-1" data-testid="appt-vehicle-readonly">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Fahrzeug</div>
              <div className="text-sm font-medium">
                {[form.vehicle_brand, form.vehicle_model].filter(Boolean).join(" ") || form.car_model || "—"}{form.vehicle_year && <span className="text-muted-foreground"> · Bj. {form.vehicle_year}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {form.plate && <span className="font-mono">{form.plate}</span>}
                {form.vehicle_color && (<><span>·</span><ColorChip value={form.vehicle_color} /></>)}
                {form.vehicle_id && (
                  <Link
                    to={`/fahrzeuge/${form.vehicle_id}`}
                    onClick={() => onOpenChange(false)}
                    className="ml-auto text-primary hover:underline inline-flex items-center gap-0.5"
                    data-testid="appt-vehicle-link"
                  >
                    → Fahrzeug öffnen
                  </Link>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground italic">Änderungen bitte im verknüpften Auftrag vornehmen.</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Fahrzeugmarke</Label>
                  <ComboBoxCreatable
                    testId="combo-brand"
                    value={form.vehicle_brand}
                    onChange={(v) => setForm((f) => ({ ...f, vehicle_brand: v, vehicle_model: "" }))}
                    options={mergedOptions(BRANDS, "brand")}
                    placeholder="Marke wählen…"
                  />
                </div>
                <div>
                  <Label>Modell</Label>
                  <ComboBoxCreatable
                    testId="combo-model"
                    value={form.vehicle_model}
                    onChange={(v) => setF("vehicle_model", v)}
                    options={mergedOptions(MODELS_BY_BRAND[form.vehicle_brand] || [], `model:${form.vehicle_brand}`)}
                    placeholder={form.vehicle_brand ? "Modell wählen…" : "Zuerst Marke wählen"}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Farbe</Label>
                  <ComboBoxCreatable
                    testId="combo-color"
                    value={form.vehicle_color}
                    onChange={(v) => setF("vehicle_color", v)}
                    options={mergedOptions(COLORS, "color")}
                    placeholder="Farbe wählen…"
                    renderValue={(v) => <ColorChip value={v} />}
                    renderOption={(o) => <ColorChip value={o.value} />}
                  />
                </div>
                <div>
                  <Label>Baujahr</Label>
                  <Input type="number" min="1900" max="2100" data-testid="input-vehicle-year" value={form.vehicle_year || ""} onChange={(e) => setF("vehicle_year", e.target.value)} placeholder="z.B. 2018" />
                </div>
                {/* 6. Kennzeichen (optional) */}
                <div>
                  <Label>Kennzeichen (optional)</Label>
                  <Input data-testid="input-plate" value={form.plate} onChange={(e) => setF("plate", e.target.value.toUpperCase())} />
                </div>
              </div>

              {/* Vehicle picker (only Arbeitstermin — links to existing Fahrzeug record) */}
              {form.terminart === "arbeitstermin" && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Fahrzeug (verknüpft, optional)</Label>
                    {form.customer_id && (
                      <Link to={`/kunden/${form.customer_id}`} className="text-xs text-primary hover:underline" data-testid="add-vehicle-link">
                        <Plus className="h-3 w-3 inline mr-0.5" /> Fahrzeug beim Kunden hinzufügen
                      </Link>
                    )}
                  </div>
                  <VehiclePicker
                    customerId={form.customer_id}
                    value={form.vehicle_id}
                    onPick={(v) => setForm((f) => ({
                      ...f,
                      vehicle_id: v.id,
                      vehicle_brand: v.manufacturer || f.vehicle_brand,
                      vehicle_model: v.model || f.vehicle_model,
                      vehicle_year: v.year || f.vehicle_year,
                      vehicle_color: v.color || f.vehicle_color,
                      car_model: `${v.manufacturer} ${v.model}`.trim(),
                      plate: v.license_plate || f.plate,
                    }))}
                    onClear={() => setF("vehicle_id", "")}
                  />
                  {form.vehicle_id && (
                    <Link to={`/fahrzeuge/${form.vehicle_id}`} className="mt-1 inline-block text-xs text-muted-foreground hover:text-foreground" data-testid="vehicle-detail-link">
                      → Fahrzeugdetails öffnen
                    </Link>
                  )}
                </div>
              )}
            </>
          )}
          </div>
          </AppointmentSection>

          <AppointmentSection
            number="3"
            icon={Wrench}
            title="Gewünschte Leistungen"
            hint="Leistungen aus dem Katalog übernehmen; Preise können angepasst werden."
          >
          {/* 7. Gewünschte Leistung */}
          {form.terminart === "arbeitstermin" ? (
            <div title="Vorläufige Anfrage – kann beim Arbeitstermin noch angepasst werden.">
              <div className="flex items-center justify-between mb-1">
                <Label>Leistungen ({(form.services || []).length})</Label>
                <ServicePicker services={services} onPick={addServiceFromCatalog} onCustom={addCustomService} />
              </div>
              {(form.services || []).length === 0 ? (
                <div className="text-xs text-muted-foreground italic px-3 py-2 border border-dashed rounded-md">
                  Noch keine Leistungen. Aus dem Katalog auswählen oder eigenen Text hinzufügen.
                </div>
              ) : (
                <div className="space-y-2">
                  {(form.services || []).map((sv, idx) => (
                    <div key={idx} className="stellar-appointment-service-row" data-testid={`appt-service-row-${idx}`}>
                      <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: sv.color || "#3377FF" }} />
                      <Input
                        value={sv.service_name}
                        onChange={(e) => updateServiceLine(idx, "service_name", e.target.value)}
                        placeholder="Leistungsbezeichnung"
                        className="stellar-appointment-service-name h-10"
                        data-testid={`appt-service-name-${idx}`}
                      />
                      <div className="stellar-appointment-service-price">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:hidden">Preis</span>
                        <Input
                          type="number" step="0.01"
                          value={sv.price}
                          onChange={(e) => updateServiceLine(idx, "price", e.target.value)}
                          placeholder="Preis"
                          className="h-10"
                          data-testid={`appt-service-price-${idx}`}
                        />
                      </div>
                      <Button
                        type="button" size="icon" variant="ghost"
                        className="stellar-appointment-service-remove"
                        onClick={() => removeServiceLine(idx)}
                        data-testid={`appt-service-remove-${idx}`}
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Gewünschte Leistung</Label>
                <ServicePicker
                  services={services}
                  onPick={(s) => setForm((f) => ({
                    ...f,
                    service_id: s.id,
                    service_name: s.name,
                  }))}
                  onCustom={(name) => setForm((f) => ({ ...f, service_id: "", service_name: name }))}
                />
              </div>
              <Input
                data-testid="input-desired-service"
                value={form.service_name}
                onChange={(e) => setF("service_name", e.target.value)}
                placeholder="z.B. Politur + Innenreinigung (kann später angepasst werden)"
              />
            </div>
          )}
          </AppointmentSection>

          <AppointmentSection
            number="4"
            icon={CalendarClock}
            title="Termin und weitere Angaben"
            hint="Datum festlegen und bei Bedarf Preis, Notizen oder Kontaktdaten ergänzen."
          >
          <div className="space-y-4">
          {/* 8. Datum und Uhrzeit — visually prominent because scheduling is critical. */}
          <div className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4 shadow-sm" data-testid="appointment-date-section">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                <CalendarClock className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest font-semibold text-blue-700">Termin</div>
                <div className="font-display text-base sm:text-lg font-semibold capitalize text-slate-900 truncate" data-testid="selected-appointment-date">
                  {appointmentDateLabel(form.start)}
                </div>
                {form.start?.slice(0, 10) !== form.end?.slice(0, 10) && (
                  <div className="text-xs text-blue-700 mt-0.5">bis {appointmentDateLabel(form.end)}</div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-blue-900">Beginn</Label>
                <Input
                  data-testid="input-start"
                  type="datetime-local"
                  value={form.start}
                  onChange={(e) => setF("start", e.target.value)}
                  className="mt-1 h-11 bg-white text-base font-medium border-blue-200 focus-visible:ring-blue-500"
                />
              </div>
              <div>
                <Label className="text-blue-900">Ende</Label>
                <Input
                  data-testid="input-end"
                  type="datetime-local"
                  value={form.end}
                  onChange={(e) => setF("end", e.target.value)}
                  className="mt-1 h-11 bg-white text-base font-medium border-blue-200 focus-visible:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-3 text-xs font-medium text-blue-700">
              Dauer: {Math.floor(durationMin/60)}h {durationMin%60}min
            </div>
          </div>

          {/* Sprint 11 — Geschätzter Preis ONLY for Arbeitstermin, and only as optional */}
          {form.terminart === "arbeitstermin" && (
            <div title="Endpreis und Zahlung werden später im Auftrag oder in der Rechnung gepflegt.">
              <Label>Geschätzter Preis (€, optional)</Label>
              <Input data-testid="input-est-price" type="number" step="0.01" value={form.estimated_price} onChange={(e) => setF("estimated_price", e.target.value)} />
            </div>
          )}

          {/* 9. Notizen */}
          <div>
            <Label>Notizen</Label>
            <Textarea data-testid="input-notes" rows={3} value={form.notes} onChange={(e) => setF("notes", e.target.value)} />
          </div>

          {/* Optionale Kontaktdaten — nicht im Telefon-Flow nötig */}
          <div className="border-t pt-3 mt-1">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Optionale Kontaktdaten</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>E-Mail (optional)</Label>
                <Input data-testid="input-email" value={form.email} onChange={(e) => setF("email", e.target.value)} />
              </div>
              <div>
                <Label>Adresse (optional)</Label>
                <Input data-testid="input-address" value={form.address} onChange={(e) => setF("address", e.target.value)} />
              </div>
            </div>
          </div>
          </div>
          </AppointmentSection>
          </fieldset>

          {appointment?.id && (
            <>
              <section className="stellar-appointment-section">
                <div className="stellar-appointment-section-heading">
                  <span className="stellar-appointment-section-icon text-violet-700 bg-violet-100"><Upload className="h-4 w-4" /></span>
                  <div>
                    <h3 className="font-display text-base font-bold" title="Bilder und Unterlagen direkt beim Termin speichern.">Fotos & Dokumente</h3>
                  </div>
                </div>
                <div className="stellar-appointment-section-content">
                <PhotoUploader
                  appointmentId={appointment.id}
                  photos={photos}
                  readOnly={isClosedJob}
                  onChanged={() => qc.invalidateQueries({ queryKey: ["appointment", appointment.id] })}
                />
                </div>
              </section>

              {/* Sprint 14 — Kostenvoranschlag (only for Begutachtung) */}
              {appointment.terminart === "begutachtung" && !isClosedJob && (
                <QuoteSection
                  appointment={refetchedAppt || appointment}
                  services={services}
                  onOpen={() => setQuoteOpen(true)}
                  onChanged={() => qc.invalidateQueries({ queryKey: ["appointment", appointment.id] })}
                />
              )}

              {!isClosedJob && <section className="stellar-appointment-section">
                <div className="stellar-appointment-section-heading">
                  <span className="stellar-appointment-section-icon text-emerald-700 bg-emerald-100"><CheckCircle2 className="h-4 w-4" /></span>
                  <div>
                    <h3 className="font-display text-base font-bold" title="Termin bestätigen oder in den nächsten Arbeitsstatus übernehmen.">Nächste Schritte</h3>
                  </div>
                </div>
                <div className="stellar-appointment-section-content grid grid-cols-1 sm:grid-cols-2 gap-2">
                {appointment.terminart === "begutachtung" && !appointment.converted_to_appointment_id && (
                  <Button type="button" variant="default" className="min-h-11" onClick={openArbeitsPrefill} data-testid="convert-to-arbeitstermin">
                    <CalendarClock className="h-4 w-4 mr-1" /> Als Arbeitstermin übernehmen
                  </Button>
                )}
                {(appointment.terminart || "arbeitstermin") === "arbeitstermin" && !(refetchedAppt?.job_id || appointment.job_id) && (
                  <Button type="button" variant="default" className="min-h-11" onClick={() => convertMut.mutate()} disabled={convertMut.isPending} data-testid="convert-to-job">
                    <Wrench className="h-4 w-4 mr-1" /> {convertMut.isPending ? "Umwandeln…" : "Zu Auftrag umwandeln"}
                  </Button>
                )}
                <Button type="button" variant="outline" className="min-h-11 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => sendWhatsApp("confirm")} data-testid="whatsapp-confirm">
                  <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp Bestätigung
                </Button>
                <Button type="button" variant="outline" className="min-h-11 border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => sendWhatsApp("reminder")} data-testid="whatsapp-reminder">
                  <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp Erinnerung
                </Button>
                </div>
              </section>}

              {/* Sprint 14 — Kostenvoranschlag editor dialog */}
              {quoteOpen && !isClosedJob && (
                <QuoteDialog
                  appointment={refetchedAppt || appointment}
                  services={services}
                  onClose={() => setQuoteOpen(false)}
                  onSaved={() => qc.invalidateQueries({ queryKey: ["appointment", appointment.id] })}
                />
              )}
            </>
          )}
        </div>

        <SheetFooter className="stellar-appointment-footer sticky bottom-0 z-20 mt-2 flex-row justify-between gap-2 border-t px-3 py-3 sm:px-6">
          {appointment?.id ? (
            <Button variant="ghost" className="text-destructive hover:bg-red-50 hover:text-red-700" onClick={() => {
              const linkedNotice = linkedJobId
                ? "\n\nDer verknüpfte Auftrag sowie Rechnungen, Zahlungen und Fotos bleiben vollständig erhalten."
                : "";
              if (confirm(`Termin wirklich aus dem Kalender löschen?${linkedNotice}`)) deleteMut.mutate();
            }} data-testid="delete-appointment">
              <Trash2 className="h-4 w-4 mr-1.5" /> Löschen
            </Button>
          ) : <div />}
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                // Sprint 22c — cancelling the conversion returns to the source Begutachtung.
                if (sourceBegutachtungId) {
                  setTimeout(() => navigate(`/kalender?open=${sourceBegutachtungId}`), 320);
                }
              }}
              data-testid="cancel-appointment"
            >Abbrechen</Button>
            {!isClosedJob && (
              <Button className="min-w-36 shadow-sm" onClick={() => saveMut.mutate()} disabled={!form.customer_name || saveMut.isPending} data-testid="save-appointment">
                {saveMut.isPending ? "Speichern…" : (appointment ? "Änderungen speichern" : "Termin speichern")}
              </Button>
            )}
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
